import { EXEC_SUMMARY, SECTIONS, APPENDICES, RECOMMENDATIONS, CLOSING_BLOCKS, THE_ASK } from "../../data/reportContent";
import { PullQuote } from "./PullQuote";
import { DataTable } from "./DataTable";
import { Visual01 } from "./Visual01";
import { Visual02 } from "./Visual02";
import { Visual03 } from "./Visual03";
import { FlowChain } from "./FlowChain";
import { PageFlowOverview } from "./PageFlowOverview";
import { CostComparison } from "./CostComparison";
import { DynamicProfileMap } from "./DynamicProfileMap";
import { ExecAsIsVisual } from "./ExecAsIsVisual";
import { ExecRoadmap } from "./ExecRoadmap";
import { AlisonMiniJourney } from "./AlisonMiniJourney";

const EXEC_VISUALS = {
  asis: ExecAsIsVisual,
  alison: AlisonMiniJourney,
  roadmap: ExecRoadmap,
  cost: CostComparison,
  northstar: DynamicProfileMap,
};

export const Block = ({ block, sectionId, i }) => {
  switch (block.type) {
    case "p":
      return (
        <p className={block.lead ? "lead-line" : undefined} data-testid={`${sectionId}-p-${i}`}>
          {block.text}
        </p>
      );
    case "quote":
      return <PullQuote text={block.text} testId={`${sectionId}-quote-${i}`} />;
    case "caption":
      return <p className="visual-note" data-testid={`${sectionId}-caption-${i}`}>{block.text}</p>;
    case "flowchain":
      return <FlowChain steps={block.steps} connector={block.connector} testId={`${sectionId}-flowchain-${i}`} />;
    case "costcomparison":
      return <CostComparison />;
    case "dynamicmap":
      return <DynamicProfileMap />;
    case "list":
      return (
        <ul className={`list-${block.variant || "stack"}`} data-testid={`${sectionId}-list-${i}`}>
          {block.items.map((it, k) => (
            <li key={k}>{it}</li>
          ))}
        </ul>
      );
    case "table":
      return <DataTable columns={block.columns} rows={block.rows} totalRow={block.totalRow} testId={`${sectionId}-table-${i}`} />;
    case "subhead":
      return <h3 className="report-subhead" data-testid={`${sectionId}-subhead-${i}`}>{block.text}</h3>;
    case "deflist":
      return (
        <dl className="report-deflist" data-testid={`${sectionId}-deflist-${i}`}>
          {block.items.map((it, k) => (
            <div className="report-deflist-row" key={k}>
              <dt>{it.term}</dt>
              <dd>{it.def}</dd>
            </div>
          ))}
        </dl>
      );
    case "callout":
      return (
        <div className="report-callout" data-testid={`${sectionId}-callout-${i}`}>
          <div className="report-callout-heading">{block.heading}</div>
          <p>{block.text}</p>
        </div>
      );
    case "apxref":
      return (
        <p className="appendix-ref" data-testid={`${sectionId}-apxref-${i}`}>
          <a href={`#${block.appendixId}`}>{block.text}</a>
        </p>
      );
    case "visual": {
      const VisualComp = EXEC_VISUALS[block.key];
      return VisualComp ? <VisualComp key={i} /> : null;
    }
    default:
      return null;
  }
};

const ExecSummary = () => (
  <section id={EXEC_SUMMARY.id} className="report-section" data-testid="section-executive-summary">
    <div className="report-section-head">
      <h2>{EXEC_SUMMARY.label}</h2>
    </div>
    {EXEC_SUMMARY.blocks.map((b, i) => (
      <Block block={b} sectionId="exec-summary" i={i} key={i} />
    ))}
  </section>
);

const VisualFlowStrip = () => (
  <div className="report-container" data-testid="visual-flow-strip">
    <div className="report-callout the-ask" data-testid="the-ask-callout">
      <div className="report-callout-heading">The ask</div>
      <p>{THE_ASK}</p>
    </div>
    <PageFlowOverview />
  </div>
);

const ReportSection = ({ section }) => (
  <section id={section.id} className="report-section" data-testid={`section-${section.id}`}>
    <div className="report-section-head">
      <span className="ghost-numeral" aria-hidden="true">{String(section.number).padStart(2, "0")}</span>
      <span className="report-section-number">{String(section.number).padStart(2, "0")}</span>
      <h2>{section.title}</h2>
    </div>
    {section.why && (
      <p className="section-why" data-testid={`${section.id}-why`}>
        <span className="section-why-label">Why this matters:</span> {section.why}
      </p>
    )}
    {section.blocks.map((b, i) => (
      <Block block={b} sectionId={section.id} i={i} key={i} />
    ))}
  </section>
);

const Recommendations = () => (
  <section id="recommendations" className="report-section report-recommendations" data-testid="section-recommendations">
    <div className="report-section-head">
      <h2>Recommendations</h2>
    </div>
    <ol>
      {RECOMMENDATIONS.map((r, i) => (
        <li key={i} data-testid={`recommendation-${i + 1}`}>
          {r}
        </li>
      ))}
    </ol>
  </section>
);

const AppendixSection = ({ appendix }) => (
  <section id={appendix.id} className="report-section appendix-section" data-testid={`section-${appendix.id}`}>
    <div className="report-section-head">
      <span className="report-section-number appendix-letter">{appendix.letter}</span>
      <h2>Appendix {appendix.letter}: {appendix.title}</h2>
    </div>
    <p className="appendix-supports" data-testid={`${appendix.id}-supports`}>Supports: {appendix.supports}</p>
    {appendix.blocks.map((b, i) => (
      <Block block={b} sectionId={appendix.id} i={i} key={i} />
    ))}
  </section>
);

const Appendices = () => (
  <div className="report-container">
    <div className="appendices-divider" data-testid="appendices-divider">
      <span>Appendices</span>
      <p className="visual-note">
        Substantiation, operational detail and assumptions supporting the core report above. No appendix introduces a
        new conclusion.
      </p>
    </div>
    {APPENDICES.map((a) => (
      <AppendixSection appendix={a} key={a.id} />
    ))}
  </div>
);

const Closing = () => (
  <div className="report-container">
    <div className="report-closing" data-testid="report-closing">
      {CLOSING_BLOCKS.map((b, i) => (
        <Block block={b} sectionId="closing" i={i} key={i} />
      ))}
    </div>
  </div>
);

export const ReportBody = () => (
  <>
    <div className="report-container">
      <ExecSummary />
    </div>
    <VisualFlowStrip />
    <div className="report-container">
      <Visual01 />
      <Visual02 />
      <Visual03 />
    </div>
    <div className="report-container">
      {SECTIONS.map((s) => (
        <ReportSection section={s} key={s.id} />
      ))}
      <Recommendations />
    </div>
    <Closing />
    <Appendices />
  </>
);
